import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Upload, Link2, Save, Image as ImageIcon } from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LangContext";
import { t } from "../../i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

export default function AdminHeroImages() {
  const { lang } = useLang();
  const [images, setImages] = useState([]);
  const [rotationHours, setRotationHours] = useState(2);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/hero-images");
      setImages(r.data?.images || []);
      setRotationHours(r.data?.rotation_hours ?? 2);
    } catch (e) {
      toast.error("Bilder konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const move = (idx, dir) => {
    const next = [...images];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setImages(next);
  };

  const remove = (idx) => {
    const url = images[idx];
    if (!window.confirm("Dieses Bild wirklich entfernen?")) return;
    setImages(images.filter((_, i) => i !== idx));
    toast.success("Bild entfernt – nicht vergessen zu speichern");
    // eslint-disable-next-line no-unused-vars
    const _ = url;
  };

  const addByUrl = () => {
    const u = newUrl.trim();
    if (!u) return;
    if (!/^https?:\/\/|^\/api\//.test(u)) {
      toast.error("URL muss mit http://, https:// oder /api/ beginnen");
      return;
    }
    setImages([...images, u]);
    setNewUrl("");
    toast.success("Bild hinzugefügt – nicht vergessen zu speichern");
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Datei zu gross (max. 8 MB)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const r = await api.post("/admin/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      setImages((prev) => [...prev, r.data.url]);
      toast.success("Bild hochgeladen – nicht vergessen zu speichern");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/hero-images", { images, rotation_hours: Number(rotationHours) });
      toast.success("Gespeichert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-5xl" data-testid="admin-hero-images-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-ink">{t(lang, "admin.hero_images")}</h1>
          <p className="text-sm text-ink-muted mt-2">Die Bilder rotieren automatisch im Hero-Bereich der Startseite.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="hero-images-save-btn"
          className="inline-flex items-center gap-2 px-5 py-3 bg-terracotta text-white rounded-full hover:bg-terracotta-hover transition disabled:opacity-50"
        >
          <Save className="w-4 h-4" strokeWidth={1.5} />
          {saving ? "Speichert…" : "Speichern"}
        </button>
      </div>

      {/* Rotation interval */}
      <div className="mt-8 bg-white border border-border rounded-md p-5 sm:p-6">
        <label className="block text-xs uppercase tracking-widest text-ink-muted mb-2">Rotationsintervall (Stunden)</label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="number"
            min="0.1"
            max="168"
            step="0.5"
            value={rotationHours}
            onChange={(e) => setRotationHours(e.target.value)}
            data-testid="hero-rotation-hours-input"
            className="w-32 px-4 py-2.5 border border-border rounded-md focus:outline-none focus:border-terracotta"
          />
          <span className="text-sm text-ink-muted">
            Voller Zyklus: {(Number(rotationHours) * images.length).toFixed(1)} h ({images.length} Bilder × {rotationHours} h)
          </span>
        </div>
      </div>

      {/* Add new image */}
      <div className="mt-6 bg-white border border-border rounded-md p-5 sm:p-6 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest text-ink-muted mb-2">Datei hochladen</label>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="hero-image-file-input" />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="hero-image-upload-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-ink/30 rounded-md text-sm hover:border-terracotta hover:bg-sand-soft transition disabled:opacity-50"
          >
            <Upload className="w-4 h-4" strokeWidth={1.5} />
            {uploading ? "Lädt hoch…" : "Bild auswählen (max. 8 MB)"}
          </button>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-ink-muted mb-2">Oder per URL hinzufügen</label>
          <div className="flex gap-2">
            <input
              type="url"
              placeholder="https://…"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addByUrl(); }}
              data-testid="hero-image-url-input"
              className="flex-1 px-4 py-2.5 border border-border rounded-md focus:outline-none focus:border-terracotta text-sm"
            />
            <button
              onClick={addByUrl}
              data-testid="hero-image-add-url-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 border border-border rounded-md hover:bg-sand-soft text-sm"
            >
              <Link2 className="w-4 h-4" strokeWidth={1.5} />
              Hinzufügen
            </button>
          </div>
        </div>
      </div>

      {/* Image grid */}
      <div className="mt-8">
        <h2 className="font-display text-xl text-ink mb-4">Aktuelle Bilder ({images.length})</h2>
        {loading ? (
          <p className="text-ink-muted">Lädt…</p>
        ) : images.length === 0 ? (
          <div className="bg-white border border-dashed border-border rounded-md p-10 text-center text-ink-muted">
            <ImageIcon className="w-8 h-8 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
            Noch keine Bilder. Füge oben Bilder hinzu.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hero-images-list">
            {images.map((url, idx) => (
              <div key={`${url}-${idx}`} className="bg-white border border-border rounded-md overflow-hidden group" data-testid={`hero-image-item-${idx}`}>
                <div className="aspect-[4/5] bg-sand-soft relative">
                  <img src={resolveUrl(url)} alt={`Hero ${idx + 1}`} className="w-full h-full object-cover" />
                  <div className="absolute top-2 left-2 bg-ink/80 text-sand text-xs px-2 py-1 rounded">#{idx + 1}</div>
                </div>
                <div className="p-3 flex items-center justify-between gap-2">
                  <div className="flex gap-1">
                    <button
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      data-testid={`hero-image-up-${idx}`}
                      className="p-2 rounded hover:bg-sand-soft disabled:opacity-30"
                      aria-label="Nach oben"
                    >
                      <ArrowUp className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => move(idx, 1)}
                      disabled={idx === images.length - 1}
                      data-testid={`hero-image-down-${idx}`}
                      className="p-2 rounded hover:bg-sand-soft disabled:opacity-30"
                      aria-label="Nach unten"
                    >
                      <ArrowDown className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                  <button
                    onClick={() => remove(idx)}
                    data-testid={`hero-image-delete-${idx}`}
                    className="p-2 rounded text-terracotta hover:bg-terracotta/10"
                    aria-label="Löschen"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
